import { Dispatch, SetStateAction, useState } from 'react';

import { useMutation } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { uploadFileToGcloud } from '@api/common';
import { checkMavenirConfigIsExist, uploadMavenirConfig } from '@api/dataSources';

import { handleErrorFromMessage } from '@utils/handleRequestError';

import { IMavenirConfigToCreate } from '@interfaces/cmModule/dataSources';
import { IFilesFiles } from '@interfaces/common/file';
import { ISetIsDirtyForm } from '@interfaces/common/handlers';

import { EFileExtensions, ESnackbarTypes } from '@enums/common';

import { HUNDRED_PERCENT_LOADING, INVALID_DATE } from '@constants/common';
import { UPLOAD_URL_ATTACHMENT } from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

interface IUseAttachFiles extends ISetIsDirtyForm {
	setConfigsToCreate: Dispatch<SetStateAction<IMavenirConfigToCreate[]>>;
}

interface IMavenirConfigUploadReturnType {
	createUploadUrlAttachment: (data: IFilesFiles) => void;
	progress: number;
}

export const getFileExtension = (file: File) => {
	const fileName = file.name;
	const extension = fileName.split('.').pop();

	return extension ? extension.toLocaleLowerCase() : '';
};

export const checkConfigName = (fileName: string) => {
	const arr = fileName.split('_');

	const timestamp = arr.at(-1) || INVALID_DATE;

	const length = arr.length;

	const date = new Date(Number(timestamp));

	const isValidLength = length >= 6 && length <= 8;

	return date.toString() !== INVALID_DATE && isValidLength;
};

export function useMutateMavenirConfigUpload(props: IUseAttachFiles): IMavenirConfigUploadReturnType {
	const [progress, setProgress] = useState<number>(HUNDRED_PERCENT_LOADING);
	const dispatch = useAppDispatch();

	const {
		setIsDirtyForm,
		setConfigsToCreate,
	} = props;

	const {
		mutate: createUploadUrlAttachment,
	} = useMutation<IFilesFiles>({
		mutationKey: [UPLOAD_URL_ATTACHMENT],
		mutationFn: async ({ files }: IFilesFiles) => {
			const promises = files.map(async (file) => {
				const fileType = getFileExtension(file);
				const formattedFileName = file.name?.slice(0, -(fileType.length + 1));
				const isValidConfigName = ('.' + fileType) !== EFileExtensions.zip ? checkConfigName(formattedFileName) : true;

				if (!isValidConfigName || !fileType.length) {
					return;
				}

				try {
					const data = await uploadMavenirConfig(file.name);
					const url = data?.uploadUrl;

					setProgress(0);

					await uploadFileToGcloud({ url, data: file });

					setIsDirtyForm(true);

					const response = await checkMavenirConfigIsExist({
						importId: data.importId,
						fileType: fileType.toUpperCase(),
						fileNames: [file.name],
					});

					const existedConfigs = response?.map((item: string) => {
						return {
							fileName: item,
							importId: data.importId,
							isSkip: true,
							file,
						};
					});

					const configs = existedConfigs.length
						? existedConfigs
						: [{
							fileName: file.name,
							importId: data.importId,
							isSkip: false,
							file,
						}];

					setConfigsToCreate(prevState => [...prevState, ...configs]);
				} catch (err: any) {
					handleErrorFromMessage(err);
					dispatch(setSnackbar({
						message: err?.message || responseMessages.error,
						type: ESnackbarTypes.error,
					}));
				}
			});

			await Promise.allSettled(promises);

			setProgress(HUNDRED_PERCENT_LOADING);
		},
	});

	return {
		createUploadUrlAttachment,
		progress,
	};
}
